// Team Privacy Types
export type TeamPrivacy = "public" | "private";

export interface TeamPrivacyType {
  id: TeamPrivacy;
  label: string;
  description: string;
  icon: string;
  graphVisibility: 'private' | 'public';
}

// Approval Response Type
export interface ApprovalResponse {
  approverId: string;
  approverEmail: string;
  approverName: string;
  response: 'approved' | 'rejected' | 'pending';
  comments?: string;
  responseDate?: string;
}

// Team Request Types
export interface TeamRequest {
  id?: string;
  azureTenantId: string; // ✨ MULTI-TENANT: Azure Tenant ID (identifiant stable)
  teamName: string;
  description: string;
  privacy: TeamPrivacy;
  justification: string;
  owner: string | Array<{ id: string; displayName: string; email: string }>; // Propriétaire au format string ou objet détaillé
  members: string;
  templateId?: string;
  quotaModelId?: string | null;
  status: RequestStatus;
  requesterId: string;
  requesterEmail: string;
  submittedDate: string;
  teamsAppId?: string; // 🔗 DEEP LINK: ID de l'application Teams pour générer les deep links dans les notifications d'approbation
  approvalId?: string; // ID de l'approbation Teams native
  approvalChatId?: string; // ID du chat de groupe des approbateurs (custom notifications)
  approvalMessageId?: string; // ID du message de la carte adaptative (custom notifications)
  approvalResponses?: ApprovalResponse[]; // Réponses individuelles des approbateurs
  processedDate?: string;
  approverName?: string;
  approverEmail?: string;
   approverComments?: string;
   createdTeamId?: string;
   errorMessage?: string;
   notificationText?: string; // Texte de notification personnalisé localisé
}

export enum RequestStatus {
  Pending = "Pending",
  Approved = "Approved",
  Rejected = "Rejected",
  Created = "Created",
  CreatingTeam = "CreatingTeam",
  Completed = "Completed",
  Failed = "Failed",
  Cancelled = "Cancelled"
}

// Administrator Types
export interface Administrator {
  id: string;
  email: string;
  displayName: string;
  addedDate: string;
}

// Configuration Types
export interface AppSettings {
  id?: string;
  azureTenantId: string; // ✨ MULTI-TENANT: Azure Tenant ID (identifiant stable)
  hostTeamId?: string; // ID de l'équipe Teams où l'app est installée
  enabledTemplates: string[];
  enabledPrivacyTypes?: TeamPrivacy[]; // Types de confidentialité activés pour les demandes (défaut: ["private", "public"])
  approvers?: Administrator[]; // Liste des approbateurs (même structure que Administrator)
  administrators?: Administrator[]; // Liste des administrateurs de l'application
  applicationName?: string; // Nom de l'application affiché dans les approbations
  autoCreateOnApproval?: boolean; // Si true, crée l'équipe immédiatement après approbation (défaut: true)
  notificationSettings?: {
    approvalMode?: "basic" | "basicAwaitAll";
    [key: string]: any;
  };
  allowedSecurityGroup?: GraphUser | null; // Groupe de sécurité autorisé à créer des équipes
  telemetryEnabled?: boolean; // Préférence de télémétrie (défaut: true si non défini)
  storageQuotaMode?: StorageQuotaMode; // 'NoQuota' par défaut côté backend
}

// Storage quota mode
export type StorageQuotaMode = 'QuotaMandatory' | 'QuotaOptional' | 'NoQuota';

// Quota model
export interface QuotaModel {
  id: string;
  azureTenantId: string;
  name: string;
  maxStorageGB: number;
  notificationsEnabled: boolean;
  notificationThresholdPercent: number | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface QuotaModelCreateInput {
  name: string;
  maxStorageGB: number;
  notificationsEnabled: boolean;
  notificationThresholdPercent?: number | null;
  isDefault?: boolean;
}

export type QuotaModelUpdateInput = Partial<QuotaModelCreateInput>;

// Info sur l'équipe hôte
export interface HostTeamInfo {
  id: string;
  displayName?: string;
  owners: TeamOwner[];
}

export interface TeamOwner {
  id: string;
  email: string;
  displayName: string;
}

// Team Template Types
export interface TeamTemplate {
  id: string;
  azureTenantId: string; // ✨ MULTI-TENANT: Azure Tenant ID (identifiant stable)
  teamId: string;
  name: string;
  description: string;
  isEnabled: boolean;
}

// Microsoft Graph Types
export interface GraphTeam {
  id: string;
  displayName: string;
  description?: string;
  visibility?: string;
}

export interface GraphUser {
  id: string;
  displayName: string;
  mail: string;
  userPrincipalName: string;
}

export interface GraphGroup {
  id: string;
  displayName: string;
  mail?: string;
  description?: string;
  mailEnabled?: boolean;
  securityEnabled?: boolean;
  groupTypes?: string[];
}

export interface GroupSetting {
  id?: string;
  displayName: string;
  templateId: string;
  values: Array<{
    name: string;
    value: string;
  }>;
}

// API Response Types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  warnings?: string[];
}

// Adaptive Card Action Types
export interface AdaptiveCardAction {
  type: "approve" | "reject";
  requestId: string;
  approverId: string;
  approverEmail: string;
  comments?: string;
  approverIds?: string[]; // Liste des IDs des approbateurs autorisés (pour validation)
}

// ===== MULTI-TENANT TYPES =====

// Tenant Configuration - Configuration complète d'un tenant
export interface TenantConfiguration {
  id?: string; // UUID Supabase (clé primaire technique)

  // ✅ CLÉ MÉTIER PRINCIPALE : Azure Tenant ID (stable, ne change jamais)
  azureTenantId: string; // Microsoft Entra Tenant ID - Utilisé comme identifiant métier

  tenantName: string; // Nom de l'organisation (peut changer)
  subdomain: string; // Sous-domaine (ex: acme) - peut changer
  azureOrganizationDisplayName?: string; // Nom récupéré depuis Graph API

  // Azure AD Configuration (optionnel pour app multi-tenant)
  azureClientId?: string; // App registration spécifique au tenant
  azureClientSecret?: string; // Chiffré

  // Microsoft Graph Configuration
  sharePointSiteUrl?: string;
  teamsAppId?: string;
  hostTeamId?: string;

  // Status
  isActive: boolean;
  provisioningStatus: 'pending' | 'provisioned' | 'error';

  // ✨ DEBUG: Active l'onglet debug pour les administrateurs
  debug?: boolean;

  // Billing (futur)
  plan?: string; // free | starter | pro | enterprise
  maxRequests?: number;

  // ✨ FREEMIUM: Subscription management fields
  licenseKey?: string;                    // For BYOL direct sales channel
  subscriptionId?: string;                // Microsoft Marketplace SaaS subscription ID
  subscriptionStatus?: SubscriptionStatus; // active, suspended, cancelled, expired
  validUntil?: string;                    // Subscription expiration date
  billingCycle?: BillingCycle;            // monthly, annual
  marketplaceOfferId?: string;            // Microsoft Marketplace offer ID
  marketplacePlanId?: string;             // Microsoft Marketplace plan ID

  // Timestamps
  createdAt?: string;
  updatedAt?: string;

  // Audit
  createdBy?: string;
  createdByEmail?: string;
}

// Azure Organization Info - Informations récupérées depuis Graph API /organization
export interface AzureOrganizationInfo {
  id: string;
  displayName: string; // Nom de l'organisation
  verifiedDomains: Array<{
    name: string; // ex: acme.com
    isDefault: boolean;
    isInitial: boolean;
  }>;
}

// Audit Log - Logs d'audit pour traçabilité
export interface AuditLog {
  id?: string;
  azureTenantId: string; // ✨ MULTI-TENANT: Azure Tenant ID (identifiant stable)

  // Event Details
  eventType: string; // tenant.created | request.submitted | team.created | etc.
  entityType?: string; // tenant | request | template | settings
  entityId?: string;

  // User Context
  userId?: string;
  userEmail?: string;

  // Changes
  oldValue?: any;
  newValue?: any;

  // Metadata
  ipAddress?: string;
  userAgent?: string;

  createdAt?: string;
}

// System Configuration - Configuration système globale
export interface SystemConfiguration {
  id?: string;
  configKey: string;
  configValue: any;
  description?: string;
  lastUpdated?: string;
  updatedBy?: string;
}

// Scheduled Job - Tâche planifiée
export interface ScheduledJob {
  id?: string;
  jobName: string;
  jobCronExpression: string;
  jobDescription?: string;
  isActive: boolean;
  lastExecution?: string;
  nextExecution?: string;
  lastStatus?: string;
  lastError?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Cron Job Types
export type CronJobType = 'team-creation' | 'orphan-tenant-detection' | 'data-cleanup' | 'notifications';

// Cron Job Configuration
export interface CronJobConfiguration {
  jobType: CronJobType;
  cronExpression: string;
  isEnabled: boolean;
  lastRun?: string;
  nextRun?: string;
}

// ===== FREEMIUM TYPES =====

export type SubscriptionPlan = 'free' | 'premium' | 'professional' | 'enterprise';
export type SubscriptionStatus = 'active' | 'suspended' | 'cancelled' | 'expired' | 'pending';
export type BillingCycle = 'monthly' | 'annual';

// Quota check result
export interface QuotaCheckResult {
  allowed: boolean;
  reason?: 'within_quota' | 'unlimited' | 'quota_exceeded' | 'subscription_expired' | 'subscription_suspended';
  currentUsage: number;
  maxAllowed: number;
  remainingRequests: number;
  resetDate: string;  // First day of next month
  plan: SubscriptionPlan;
  upgradeRequired: boolean;
}

// Usage statistics for dashboard
export interface UsageStats {
  currentMonth: {
    used: number;
    limit: number;
    percentage: number;
  };
  plan: SubscriptionPlan;
  subscriptionStatus: SubscriptionStatus;
  validUntil?: string;
  resetDate: string;
  isStripeSubscription?: boolean;
}

// License activation request
export interface LicenseActivationRequest {
  licenseKey: string;
}

// License activation response
export interface LicenseActivationResponse {
  success: boolean;
  plan?: SubscriptionPlan;
  validUntil?: string;
  maxRequests?: number;
  error?: string;
}

// Microsoft Marketplace webhook payload
export interface MarketplaceWebhookPayload {
  id: string;
  activityId: string;
  subscriptionId: string;
  offerId: string;
  planId: string;
  publisherId: string;
  quantity?: number;
  action: 'Subscribe' | 'Unsubscribe' | 'ChangePlan' | 'ChangeQuantity' | 'Suspend' | 'Reinstate' | 'Renew';
  timeStamp: string;
  status: string;
  purchaser: {
    emailId: string;
    objectId: string;
    tenantId: string;
  };
  beneficiary: {
    emailId: string;
    objectId: string;
    tenantId: string;
  };
}

// Subscription upgrade request
export interface SubscriptionUpgradeRequest {
  targetPlan: SubscriptionPlan;
  billingCycle?: BillingCycle;
  returnUrl?: string;
}

// API Error codes for freemium
export type FreemiumErrorCode = 
  | 'QUOTA_EXCEEDED'
  | 'SUBSCRIPTION_EXPIRED'
  | 'SUBSCRIPTION_SUSPENDED'
  | 'INVALID_LICENSE_KEY'
  | 'LICENSE_ALREADY_USED'
  | 'LICENSE_EXPIRED';
